import { AudioClips } from "../../../audio/audio_constants.js";
import { audioController } from "../../../audio/audio_controller.js";
import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIProgressBar } from "../../common_ui_elements/ui_progress_bar.js";
import { UIScrollableContainer } from "../../common_ui_elements/ui_scrollable_container.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { SafeAreas } from "../../safe_areas.js";
import { DailyTaskCard, DailyTaskCardConfig, DailyTaskReward, DailyTaskState } from "./daily_task_card.js";
import { DailyTasksService } from "../../../daily_tasks/daily_tasks_service.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { isTimedRewardType, rewardAmountText } from "../../../common/reward_format.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import type { DailyTaskDto, DailyTasksPayloadDto, WeeklyStarsDto } from "../../../api/dto/daily_tasks.dto.js";
import { eventPayloadKey } from "../../../api/event_payload_key.js";
import { StarChestOpenPopup, dailyTaskRewardIcon } from "./star_chest_open_popup.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import type { RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import { StarFly } from "../../reward_fly/star_fly.js";
import { AdventureTooltip } from "../../tooltip/adventure_tooltip.js";
import { TooltipManager } from "../../tooltip/tooltip_manager.js";
import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { DailyTasksAnalytics } from "../../../daily_tasks/daily_tasks_analytics.js";

const TASK_CARD_WIDTH_DESIGN = 940;
const TASK_CARD_HEIGHT_DESIGN = 173.055;
const TASKS_VIEWPORT_BASE_HEIGHT_DESIGN = 662.22;
const VISIBLE_TASKS_COUNT = 4;
const NEXT_TASK_PREVIEW_HEIGHT_DESIGN = 18;
const TASKS_BACKGROUND_UP_SHIFT_DESIGN = 12;
const TASKS_VIEWPORT_BOTTOM_EXTENSION_DESIGN = 16;
const REWARD_PROGRESS_GROUP_OFFSET_X_DESIGN = 14.5;
// Reserve a footer inside the outer blue panel: task cards stop above it and
// the day selector sits near the bottom edge, as in the reference layout.
const DAY_TABS_BASE_UP_SHIFT_DESIGN = 70;
const DAY_TABS_FOOTER_RESERVE_DESIGN = 70;
const CONTENT_BACKGROUND_BOTTOM_MARGIN_DESIGN = 62;
// Room inside the viewport above and to the right of the cards: the padlock
// of a locked day's card overhangs the card's top-right corner.
const TASKS_VIEWPORT_TOP_PAD_DESIGN = 16;
const TASKS_VIEWPORT_RIGHT_PAD_DESIGN = 22;
const TASKS_VIEWPORT_CORNER_RADIUS_DESIGN = 60;
const TASKS_FOUR_CARD_EXTRA_HEIGHT_DESIGN =
  TASK_CARD_HEIGHT_DESIGN * VISIBLE_TASKS_COUNT - TASKS_VIEWPORT_BASE_HEIGHT_DESIGN;

// Reading order inside a day: what the player can collect right now, then what
// is still in progress, then what is already done and needs no attention.
const TASK_STATE_ORDER: Record<DailyTaskState, number> = {
  [ DailyTaskState.Completed ]: 0,
  [ DailyTaskState.Progress ]: 1,
  [ DailyTaskState.Claimed ]: 2,
};

function sortTasksForDisplay( tasks: DailyTaskCardConfig[] ): DailyTaskCardConfig[]
{
  // Stable sort: inside one bucket the configured order from the server wins.
  return [ ...tasks ].sort(
    ( a, b ) => TASK_STATE_ORDER[a.state] - TASK_STATE_ORDER[b.state]
  );
}

interface DailyTaskDayConfig
{
  day: number;
  /** A day after today: its tasks can be viewed and progressed, not claimed. */
  locked: boolean;
  /** Rewards ready to claim right now (the red badge); always 0 on a locked day. */
  completed_tasks: number;
  tasks: DailyTaskCardConfig[];
}

function createDayConfig(
  day: number,
  locked: boolean,
  tasks: DailyTaskCardConfig[]
): DailyTaskDayConfig
{
  return {
    day,
    locked,
    completed_tasks: locked ? 0 : countClaimable( tasks ),
    tasks,
  };
}

function countClaimable( tasks: readonly { state: DailyTaskState }[] ): number
{
  return tasks.filter( task => task.state === DailyTaskState.Completed ).length;
}

/** Base visual shell for the daily tasks feature. */
export class DailyTasksScreen extends UIElement
{
  private events_controller: BasicEventsController | null = null;
  private reward_progress_bar: UIProgressBar | null = null;
  private progress_star: UIImage | null = null;
  private tasks_scroll_container: UIScrollableContainer | null = null;
  private task_cards: DailyTaskCard[] = [];
  private content_root?: UIElementHolder;
  private day_tabs_background_layer?: UIElementHolder;
  private day_tabs_foreground_layer?: UIElementHolder;
  // Filled from the server. Days up to today are playable and claimable;
  // later days can be opened to look at their tasks, whose progress already
  // counts, but their rewards are claimed only once that day starts.
  private day_configs: DailyTaskDayConfig[] = Array.from(
    { length: 7 },
    ( _, index ) => createDayConfig( index + 1, index > 0, [] )
  );
  private today_day = 1;
  private selected_day = 1;
  private empty_day_text?: UITextElement;
  private week_end_ms = 0;
  private rollover_attempt_at = 0;
  private timer_text?: UITextElement;
  private last_timer_string = "";
  private stars_view: WeeklyStarsDto | null = null;
  private stars_layer?: UIElementHolder;
  private content_width = 0;
  private tasks_panel_extra_height = 0;
  private day_tabs_up_shift_design = DAY_TABS_BASE_UP_SHIFT_DESIGN;

  private popup_layer?: UIElementHolder;
  private chest_claim_inflight = false;
  private reward_popup_open = false;
  private payload_key = "";

  private chest_popup?: StarChestOpenPopup;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController();
    this.createBackground();

    // Keep the authored 1080px-wide composition intact on wider screens.
    // Only the solid blue screen background is allowed to fill the viewport.
    this.content_width = Math.min( this.rect.getW(), LayoutScaler.widthFromDesign( 1080 ) );
    // rect.getH() already excludes both safe areas. Deriving the adaptive
    // extension from the full viewport made the bottom content overflow by
    // exactly those insets on APK/Telegram.
    const visible_content_design_height = this.rect.getH() / LayoutScaler.getScale();
    this.tasks_panel_extra_height = TASKS_FOUR_CARD_EXTRA_HEIGHT_DESIGN
      + Math.max( 0, visible_content_design_height - 1920 );
    // On a short safe-area viewport lift only the missing part; tall screens
    // keep the selector close to the rounded bottom edge.
    this.day_tabs_up_shift_design = DAY_TABS_BASE_UP_SHIFT_DESIGN
      + Math.max( 0, 1920 - visible_content_design_height );
    this.content_root = new UIElementHolder(
      this.ctx,
      ( this.rect.getW() - this.content_width ) / 2,
      0,
      this.content_width,
      this.rect.getH()
    );
    super.addChild( this.content_root );

    this.createTopBackground();
    this.createContentBackground();
    this.createDayTabsBackgroundLayer();
    this.createDaySelectorBackgrounds();
    this.createTasksBackground();
    this.createTitle();
    this.createTimerBackground();
    this.createRewardProgressBar();
    this.createRewardProgressStar();
    this.createStarsLayer();
    this.createTasksScrollContainer();
    this.createTaskCards();
    this.createDayTabsForegroundLayer();
    this.createCurrentDayBackgroundShadowCover();
    this.createDaySelector();
    this.createDaySelectorStatuses();
    this.createHeaderButtons();

    // Chest rewards are a modal experience, so their dim backdrop must cover
    // the physical canvas too. Keeping this layer inside the content rect
    // leaves the top safe-area/notch visibly undimmed.
    this.popup_layer = new UIElementHolder(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      false
    );
    this.popup_layer.setClickable( false );
    super.addChild( this.popup_layer );

    this.chest_popup = new StarChestOpenPopup( this.ctx, this.popup_layer );

    void this.loadFromServer();
  }

  private async loadFromServer( force = true ): Promise<void>
  {
    // The cached payload is drawn at once; the server is asked afterwards
    // (a conditional GET, 304 when nothing changed) and the screen rebuilds
    // only when the payload actually differs. The week rollover path passes
    // force=false so the service's expired-retry throttle owns the request rate.
    const cached = DailyTasksService.getCached();
    if ( cached )
      this.applyPayload( cached );

    const payload = await DailyTasksService.fetch( force );
    if ( payload )
      this.applyPayload( payload );
  }

  private applyPayload( payload: DailyTasksPayloadDto ): void
  {
    const payload_key = eventPayloadKey( payload );
    if ( payload_key === this.payload_key )
      return;
    const first_payload = this.payload_key === "";
    this.payload_key = payload_key;

    const current_day = Math.min( 7, Math.max( 1, ( payload.day_index ?? 0 ) + 1 ) );
    // A refresh keeps the day the player is looking at; a new day jumps to it.
    if ( first_payload || current_day !== this.today_day )
      this.selected_day = current_day;
    this.today_day = current_day;
    // Weekly event with catch-up: elapsed days stay open and playable until
    // the week ends; future days are viewable but their rewards stay locked.
    this.day_configs = Array.from( { length: 7 }, ( _, index ) =>
    {
      const day = index + 1;
      const locked = day > current_day;
      const tasks = payload.tasks.filter( task => ( task.day ?? current_day ) === day );
      return createDayConfig(
        day,
        locked,
        sortTasksForDisplay(
          tasks.map( task => DailyTasksScreen.toCardConfig( task, locked ? day : null ) )
        )
      );
    } );
    // The countdown tracks the WEEK, not the daily reset: past days stay
    // playable and claimable until the personal week ends.
    this.week_end_ms = payload.stars?.week_end ? Date.parse( payload.stars.week_end ) : 0;
    this.stars_view = payload.stars ?? null;

    this.createTaskCards();
    this.renderStars();
    this.day_tabs_background_layer?.removeAllChildren();
    this.day_tabs_foreground_layer?.removeAllChildren();
    this.createDaySelectorBackgrounds();
    this.createCurrentDayBackgroundShadowCover();
    this.createDaySelector();
    this.createDaySelectorStatuses();
    this.updateTimerText( performance.now() );
    this.forceMakeShouldBeRendered();
  }

  private static toCardConfig( task: DailyTaskDto, locked_until_day: number | null ): DailyTaskCardConfig
  {
    const primary = task.rewards[0] ?? { type: "coin", amount: 0 };
    const reward: DailyTaskReward =
      primary.type === "coin" ? "coins"
        : primary.type === "fireworks" ? "firework"
          : primary.type;
    const is_booster = primary.type !== "coin" && primary.type !== "heart" && !isTimedRewardType( primary.type );

    return {
      task_id: task.id,
      title_text: DailyTasksScreen.taskTitle( task ),
      state: task.claimed
        ? DailyTaskState.Claimed
        : task.completed
          ? DailyTaskState.Completed
          : DailyTaskState.Progress,
      progress: Math.min( task.progress, task.target ),
      target: task.target,
      stars: task.stars ?? 5,
      reward,
      metric: task.metric,
      metric_param: task.param,
      reward_text: isTimedRewardType( primary.type )
        ? rewardAmountText( primary.type, primary.amount )
        : is_booster ? `x${primary.amount}` : String( primary.amount ),
      locked_until_day,
    };
  }

  private static taskTitle( task: DailyTaskDto ): string
  {
    const base_key = `DAILY_TASKS.TASK.${task.metric.toUpperCase()}`;
    if ( task.param )
    {
      const param_label = LocalizationManager.t(
        `DAILY_TASKS.PARAM.${task.param.toUpperCase()}`
      );
      return LocalizationManager.tWithParams(
        `${base_key}_PARAM`,
        [ String( task.target ), param_label ]
      );
    }
    return LocalizationManager.tWithParams( base_key, [ String( task.target ) ] );
  }

  private updateTimerText( now: number ): void
  {
    if ( !this.timer_text || this.week_end_ms <= 0 ) return;

    const text = formatShortDuration( this.week_end_ms - Date.now() );
    if ( text === this.last_timer_string ) return;

    this.last_timer_string = text;
    this.timer_text.setText( text );
    this.forceMakeShouldBeRendered();
  }

  private createBackground(): void
  {
    const full_x = -Game.safeAreaLeft();
    const full_y = -Game.safeAreaTop();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const full_h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();

    const background = new RoundedRectangle(
      this.ctx,
      full_x,
      full_y,
      full_w,
      full_h,
      0,
      "#0849B9"
    );
    super.addChild( background );
  }

  private createTopBackground(): void
  {
    const background = new UIImage(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 125 ),
      this.content_width,
      LayoutScaler.heightFromDesign( 689 ),
      ImgID.UI_DailyTasksScreenTopBg
    );
    this.addChild( background );
  }

  private createContentBackground(): void
  {
    const y = LayoutScaler.yFromDesign( 700 );
    const bottom_margin = LayoutScaler.yFromDesign(
      CONTENT_BACKGROUND_BOTTOM_MARGIN_DESIGN
    );

    const background = new UINineSliceElement(
      this.ctx,
      0,
      y,
      this.content_width,
      Math.max( LayoutScaler.heightFromDesign( 825 ), this.rect.getH() - y - bottom_margin ),
      ImgID.UI_DailyTasksScreenContentBg,
      "capsule_bg"
    );
    this.addChild( background );
  }

  private createTasksBackground(): void
  {
    const background = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 43 ),
      LayoutScaler.yFromDesign( 968 - TASKS_BACKGROUND_UP_SHIFT_DESIGN ),
      LayoutScaler.widthFromDesign( 996 ),
      LayoutScaler.heightFromDesign(
        738.22 + this.tasks_panel_extra_height + TASKS_BACKGROUND_UP_SHIFT_DESIGN
          - DAY_TABS_FOOTER_RESERVE_DESIGN
      ),
      ImgID.UI_DailyTasksTasksBg,
      "tasks_bg"
    );
    this.addChild( background );
  }

  private createTitle(): void
  {
    const w = LayoutScaler.widthFromDesign( 712 );
    const h = LayoutScaler.heightFromDesign( 190 );
    const x = this.content_width / 2 - w / 2;
    const y = LayoutScaler.yFromDesign( 63 );

    const title_bg = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_DailyTasksScreenTitleBg
    );
    this.addChild( title_bg );

    const title_lower_outline = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 101 ),
      LayoutScaler.yFromDesign( 31 ),
      LayoutScaler.widthFromDesign( 510 ),
      LayoutScaler.heightFromDesign( 128 ),
      {
        localization_key: "DAILY_TASKS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
        font_weight: 900,
        text_color: "rgba(0, 0, 0, 0)",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: "#05378B",
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_blur: 0,
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    title_bg.addChild( title_lower_outline, true );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 101 ),
      LayoutScaler.yFromDesign( 31 ),
      LayoutScaler.widthFromDesign( 510 ),
      LayoutScaler.heightFromDesign( 128 ),
      {
        localization_key: "DAILY_TASKS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFFFF" },
            { offset: 1, color: "#FFDE8D" },
          ],
        },
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#05378B",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_color: "#C75C00",
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    title_bg.addChild( title, true );
  }

  private createTimerBackground(): void
  {
    const w = LayoutScaler.widthFromDesign( 377 );
    const h = LayoutScaler.heightFromDesign( 127 );

    const timer_bg = new UIImage(
      this.ctx,
      this.content_width / 2 - w / 2,
      LayoutScaler.yFromDesign( 637 ),
      w,
      h,
      ImgID.UI_DailyTasksScreenTimerBg
    );
    this.addChild( timer_bg );

    const pill_scale = 127 / 130;
    const timer = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 142 ),
      LayoutScaler.yFromDesign( 44 * pill_scale ),
      LayoutScaler.widthFromDesign( 150 ),
      LayoutScaler.heightFromDesign( 66 * pill_scale ),
      {
        text: "--:--",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        center_on_cap_height: true,
        ...TextStyleScheme.timerShadow(),
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    timer_bg.addChild( timer );
    this.timer_text = timer;
  }

  private createHeaderButtons(): void
  {
    // daily_info.png has considerably wider transparent padding than the
    // close asset. 146px makes both opaque button circles about 104px while
    // preserving the original info-button center from the 120px Figma frame.
    const info_size = LayoutScaler.widthFromDesign( 146 );
    const info_x = LayoutScaler.xFromDesign( 14 );
    const info_y = LayoutScaler.yFromDesign( 42 );

    const info = new UIImage(
      this.ctx,
      info_x,
      info_y,
      info_size,
      info_size,
      ImgID.UI_DailyTasksScreenInfo,
      {},
      true
    );
    info.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      OverlayFactory.openOverlay( OverlayId.DAILY_TASKS_OVERLAY );
    } );
    // Header controls stay attached to the full-width blue background.
    super.addChild( info );

    const close_size = LayoutScaler.widthFromDesign( 110 );
    const close = new UIImage(
      this.ctx,
      this.rect.getW() - close_size - LayoutScaler.xFromDesign( 22 ),
      LayoutScaler.yFromDesign( 60 ),
      close_size,
      close_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    close.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void ScreenManager.showScreen( Screens.MENU );
    } );
    super.addChild( close );
  }

  private createRewardProgressBar(): void
  {
    const design_x = 170 + REWARD_PROGRESS_GROUP_OFFSET_X_DESIGN;
    // Leave a visible tail after the last 100px-wide milestone marker. The
    // previous 874px endpoint stopped before its 918px center.
    const w = LayoutScaler.widthFromDesign( 960 - 170 );
    const h = LayoutScaler.heightFromDesign( 50 );
    const x = LayoutScaler.xFromDesign( design_x );
    const y = LayoutScaler.yFromDesign( 819 );

    this.reward_progress_bar = new UIProgressBar(
      this.ctx,
      x,
      y,
      w,
      h,
      {
        base_img_id: ImgID.UI_DailyTasksProgressBase,
        fill_img_id: ImgID.UI_DailyTasksProgressFill,
        base_nine_slice: "daily_tasks_pb_base",
        fill_nine_slice: "daily_tasks_pb_fill",
        border_width: 0,
        fill_height: LayoutScaler.heightFromDesign( 36 ),
        fill_offset_left: LayoutScaler.widthFromDesign( 10 ),
        fill_offset_right: LayoutScaler.widthFromDesign( 10 ),
        // Preserve the source cap aspect ratios after resizing both images.
        base_corner_sizes: {
          top_left: {
            width: LayoutScaler.widthFromDesign( 50 * 50 / 60 ),
            height: LayoutScaler.heightFromDesign( 25 ),
          },
          bottom_right: {
            width: LayoutScaler.widthFromDesign( 50 * 50 / 60 ),
            height: LayoutScaler.heightFromDesign( 25 ),
          },
        },
        fill_corner_sizes: {
          top_left: {
            width: LayoutScaler.widthFromDesign( 39 * 36 / 40 ),
            height: LayoutScaler.heightFromDesign( 18 ),
          },
          bottom_right: {
            width: LayoutScaler.widthFromDesign( 39 * 36 / 40 ),
            height: LayoutScaler.heightFromDesign( 18 ),
          },
        },
        initial_progress: 0.52,
        shrink_fill_to_zero: true,
      }
    );
    this.reward_progress_bar.setClickable( false );
    this.addChild( this.reward_progress_bar );
  }

  public setRewardProgress( current: number, target: number ): void
  {
    const progress = target > 0 ? current / target : 0;
    this.reward_progress_bar?.setProgress( progress );
  }

  private createRewardProgressStar(): void
  {
    const star = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 70.5 + REWARD_PROGRESS_GROUP_OFFSET_X_DESIGN ),
      LayoutScaler.yFromDesign( 746 ),
      LayoutScaler.widthFromDesign( 150 ),
      LayoutScaler.heightFromDesign( 150 ),
      ImgID.UI_DailyTasksProgressStar
    );
    this.addChild( star );
    this.progress_star = star;
  }

  private createStarsLayer(): void
  {
    this.stars_layer = new UIElementHolder(
      this.ctx, 0, 0, this.content_width, this.rect.getH()
    );
    this.addChild( this.stars_layer );
    this.renderStars();
  }

  /** Weekly meta-progress: star bar, chest markers, threshold labels. */
  private renderStars(): void
  {
    if ( !this.stars_layer ) return;
    this.stars_layer.removeAllChildren();

    const view = this.stars_view;
    const chests = view?.chests ?? [];
    const current = view?.current ?? 0;
    const max_threshold = chests.length
      ? chests[chests.length - 1].stars
      : 1;
    this.reward_progress_bar?.setProgress(
      Math.max( 0, Math.min( 1, current / max_threshold ) )
    );

    // Current stars under the big star on the left.
    this.addStarsLabel( String( current ), 143 + REWARD_PROGRESS_GROUP_OFFSET_X_DESIGN );

    // Up to three chest slots spread along the bar.
    const slot_centers = [ 398.65, 644.09, 903.59 ].map(
      center => center + REWARD_PROGRESS_GROUP_OFFSET_X_DESIGN
    );
    const marker_y = 750;
    chests.forEach( ( chest, index ) =>
    {
      const center_x = slot_centers[Math.min( index, slot_centers.length - 1 )];

      if ( chest.claimed )
      {
        const done_width = 100;
        const done_height = 89;
        const done = new UIImage(
          this.ctx,
          LayoutScaler.xFromDesign( center_x - done_width / 2 ),
          // Centre the claimed marker on the 50px-high progress bar at y=819.
          LayoutScaler.yFromDesign( 819 + ( 50 - done_height ) / 2 ),
          LayoutScaler.widthFromDesign( done_width ),
          LayoutScaler.heightFromDesign( done_height ),
          ImgID.UI_DailyTasksProgressConfirm
        );
        done.setClickable( false );
        this.stars_layer!.addChild( done );
      }
      else
      {
        // Locked chests are NOT dimmed by design — every unclaimed chest looks
        // the same; a claimed one turns into the open/checkmark state.
        const size_w = 154.17;
        const size_h = 142.33;
        const chest_center_x = LayoutScaler.xFromDesign( center_x );
        const chest_center_y = LayoutScaler.yFromDesign( marker_y + size_h / 2 );

        if ( chest.claimable )
          this.startClaimableChestGlow( chest_center_x, chest_center_y );

        const chest_top = LayoutScaler.yFromDesign( marker_y );
        const chest_height = LayoutScaler.heightFromDesign( size_h );
        const chest_image = new UIImage(
          this.ctx,
          LayoutScaler.xFromDesign( center_x - size_w / 2 ),
          chest_top,
          LayoutScaler.widthFromDesign( size_w ),
          chest_height,
          ImgID.UI_DailyTasksRewardChest,
          {},
          true
        );
        if ( chest.claimable )
        {
          chest_image.setOnClick( () => this.claimChest( index ) );
          this.startClaimableChestBounce( chest_image, chest_top, chest_height );
        }
        else
        {
          chest_image.setOnClick( () =>
            this.showChestRewardsTooltip( chest_image, chest.rewards )
          );
        }
        this.stars_layer!.addChild( chest_image );
      }

      this.addStarsLabel( String( chest.stars ), center_x );
    } );
  }

  private showChestRewardsTooltip(
    chest_image: UIImage,
    rewards: DailyTaskRewardDto[]
  ): void
  {
    if ( rewards.length === 0 ) return;

    const tooltip_content = rewards.map( reward => ( {
      img_id: ADVENTURE_REWARD_ICONS[reward.type],
      count: reward.amount,
      amount_text: reward.type === "coin"
        ? String( reward.amount )
        : rewardAmountText( reward.type, reward.amount ),
    } ) );
    const { width, height } = AdventureTooltip.computeSize( rewards.length );
    const chest_abs = chest_image.getAbsolutePosition();
    const anchor_x = chest_abs.x + chest_image.getRect().getW() / 2;
    const screen_margin = LayoutScaler.widthFromDesign( 18 );
    const tooltip_x = Math.max(
      screen_margin,
      Math.min( Game.screenW() - width - screen_margin, anchor_x - width / 2 )
    );
    const tail_margin = LayoutScaler.widthFromDesign( 70 );
    const tail_x = Math.max(
      tail_margin,
      Math.min( width - tail_margin, anchor_x - tooltip_x )
    );
    const tooltip_y = Math.max(
      LayoutScaler.yFromDesign( 8 ),
      chest_abs.y - height + LayoutScaler.yFromDesign( 20 )
    );

    TooltipManager.openTooltip( new AdventureTooltip(
      TooltipManager.ctx,
      tooltip_x,
      tooltip_y,
      width,
      height,
      tail_x,
      tooltip_content
    ) );
  }

  private static readonly CLAIMABLE_GLOW_SIZE = 233;
  private static readonly CLAIMABLE_GLOW_MIN_ALPHA = 0.45;
  private static readonly CLAIMABLE_GLOW_MAX_ALPHA = 0.95;
  private static readonly CLAIMABLE_GLOW_PULSE_MS = 900;
  private static readonly CLAIMABLE_GLOW_ROTATION_MS = 14000;

  private static readonly CLAIMABLE_BOUNCE_SQUASH_SCALE = 0.8;
  private static readonly CLAIMABLE_BOUNCE_STRETCH_SCALE = 1.2;
  private static readonly CLAIMABLE_BOUNCE_HEIGHT = 16;

  private static readonly CLAIMABLE_BOUNCE_SQUASH_MS = 130;
  private static readonly CLAIMABLE_BOUNCE_LAUNCH_MS = 170;
  private static readonly CLAIMABLE_BOUNCE_FALL_MS = 170;
  private static readonly CLAIMABLE_BOUNCE_LAND_MS = 130;
  private static readonly CLAIMABLE_BOUNCE_SETTLE_MS = 150;

  private static readonly CLAIMABLE_BOUNCE_COUNT = 1;
  private static readonly CLAIMABLE_BOUNCE_PAUSE_MS = 1800;

  /** Same tinted spinning-rays particle as StarChestOpenPopup's own reveal
   * glow (reuses its cached tint via chest_popup so it's not recomputed
   * here) — just pulsing instead of fading in once. */
  private startClaimableChestGlow( center_x: number, center_y: number ): void
  {
    const size = LayoutScaler.widthFromDesign( DailyTasksScreen.CLAIMABLE_GLOW_SIZE );
    const glow = new UIElementHolder( this.ctx, center_x - size / 2, center_y - size / 2, size, size, false );
    glow.setClickable( false );
    glow.setOpacityImmediate( DailyTasksScreen.CLAIMABLE_GLOW_MIN_ALPHA );

    const tinted_bitmap = this.chest_popup?.getTintedGlowBitmap() ?? null;
    const now = performance.now();

    for ( const direction of [ 1, -1 ] )
    {
      const layer = new UIImage( this.ctx, 0, 0, size, size, ImgID.RaysGlow1 );
      if ( tinted_bitmap )
        layer.setImageBitmap( tinted_bitmap );

      layer.enableRotation();
      layer.addTransform( new TransformRotate(
        layer, true, 0, direction * Math.PI * 2, now, DailyTasksScreen.CLAIMABLE_GLOW_ROTATION_MS,
        { easing: Easing.Linear, infinite_loop: true, decorative: true }
      ) );
      glow.addChild( layer );
    }

    this.stars_layer!.addChild( glow );

    glow.addTransform( new TransformOpacity(
      glow, true,
      DailyTasksScreen.CLAIMABLE_GLOW_MIN_ALPHA, DailyTasksScreen.CLAIMABLE_GLOW_MAX_ALPHA,
      now, DailyTasksScreen.CLAIMABLE_GLOW_PULSE_MS,
      { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true, infinite_loop: true, decorative: true }
    ) );
  }

  private startClaimableChestBounce( chest_image: UIImage, base_y: number, height: number ): void
  {
    const x = chest_image.pixelPosImportant().x;
    const ground_y = base_y + height;
    const hop_rise = LayoutScaler.heightFromDesign( DailyTasksScreen.CLAIMABLE_BOUNCE_HEIGHT );

    const SQUASH = DailyTasksScreen.CLAIMABLE_BOUNCE_SQUASH_SCALE;
    const STRETCH = DailyTasksScreen.CLAIMABLE_BOUNCE_STRETCH_SCALE;

    const apply = ( scale_y: number, bottom_y: number ): void =>
    {
      chest_image.setScale( new Vector2( 1, scale_y ) );
      chest_image.setPixelPos( new Vector2( x, bottom_y - height * scale_y ), true );
    };
    const sample = ( scale_y: number, bottom_y: number ) => ( {
      scale: new Vector2( 1, scale_y ),
      pos: new Vector2( x, bottom_y - height * scale_y ),
    } );

    const squashPhase = ( from: number, to: number, duration_ms: number, easing: Easing, on_finished: () => void ): void =>
    {
      const tr = new TransformValue( from, to, performance.now(), duration_ms, scale_y => apply( scale_y, ground_y ), { easing } );
      tr.setDomSample( scale_y => sample( scale_y, ground_y ) );
      tr.setOnFinished( on_finished );
      chest_image.addTransform( tr );
    };

    const cycle = ( remaining: number ): void =>
    {
      if ( remaining <= 0 )
      {
        window.setTimeout( () => cycle( DailyTasksScreen.CLAIMABLE_BOUNCE_COUNT ), DailyTasksScreen.CLAIMABLE_BOUNCE_PAUSE_MS );
        return;
      }

      // 1) crouch
      squashPhase( 1, SQUASH, DailyTasksScreen.CLAIMABLE_BOUNCE_SQUASH_MS, Easing.EaseOutQuad, () =>
      {
        // 2) launch: stretch and rise together
        const launch = new TransformValue( 0, 1, performance.now(), DailyTasksScreen.CLAIMABLE_BOUNCE_LAUNCH_MS, t =>
          apply( SQUASH + ( STRETCH - SQUASH ) * t, ground_y - hop_rise * t ),
          { easing: Easing.EaseOutQuad }
        );
        launch.setDomSample( t => sample( SQUASH + ( STRETCH - SQUASH ) * t, ground_y - hop_rise * t ) );
        launch.setOnFinished( () =>
        {
          // 3) fall back down, still stretched
          const fall = new TransformValue( 0, 1, performance.now(), DailyTasksScreen.CLAIMABLE_BOUNCE_FALL_MS, t =>
            apply( STRETCH, ( ground_y - hop_rise ) + hop_rise * t ),
            { easing: Easing.EaseInQuad }
          );
          fall.setDomSample( t => sample( STRETCH, ( ground_y - hop_rise ) + hop_rise * t ) );
          fall.setOnFinished( () =>
          {
            // 4) impact squash, 5) settle back to normal, then pause and repeat
            squashPhase( STRETCH, SQUASH, DailyTasksScreen.CLAIMABLE_BOUNCE_LAND_MS, Easing.EaseOutQuad, () =>
              squashPhase( SQUASH, 1, DailyTasksScreen.CLAIMABLE_BOUNCE_SETTLE_MS, Easing.EaseOutQuad, () => cycle( remaining - 1 ) )
            );
          } );
          chest_image.addTransform( fall );
        } );
        chest_image.addTransform( launch );
      } );
    };

    cycle( DailyTasksScreen.CLAIMABLE_BOUNCE_COUNT );
  }

  private addStarsLabel( text: string, center_x: number ): void
  {
    const label_w = LayoutScaler.widthFromDesign( 120 );
    const label = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( center_x ) - label_w / 2,
      LayoutScaler.yFromDesign( 854.36 + 35 ),
      label_w,
      LayoutScaler.heightFromDesign( 61 ),
      {
        text,
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: "#0E2D92",
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    label.setClickable( false );
    this.stars_layer!.addChild( label );
  }

  private claimChest( index: number ): void
  {
    if ( this.chest_claim_inflight ) return;
    this.chest_claim_inflight = true;

    void audioController.playSound( AudioClips.UI_CLICK );
    void DailyTasksService.claimChest( index ).then( rewards =>
    {
      this.chest_claim_inflight = false;
      if ( !rewards ) return;

      this.stars_view = DailyTasksService.getCached()?.stars ?? this.stars_view;
      this.renderStars();
      this.forceMakeShouldBeRendered();

      if ( this.popup_layer && this.chest_popup )
      {
        this.reward_popup_open = true;
        this.popup_layer.setClickable( true );
        this.chest_popup.open( rewards, () =>
        {
          this.reward_popup_open = false;
          this.popup_layer?.setClickable( false );
        } );
      }
    } );
  }

  private createTasksScrollContainer(): void
  {
    // Keep scrolling cards inside the rounded inner edge of tasks_bg.
    const viewport_x = 71;
    const viewport_y = 1001 - NEXT_TASK_PREVIEW_HEIGHT_DESIGN - TASKS_VIEWPORT_TOP_PAD_DESIGN;
    const viewport_width = TASK_CARD_WIDTH_DESIGN + TASKS_VIEWPORT_RIGHT_PAD_DESIGN;
    const viewport_height = TASKS_VIEWPORT_TOP_PAD_DESIGN
      + TASKS_VIEWPORT_BASE_HEIGHT_DESIGN
      + this.tasks_panel_extra_height
      + NEXT_TASK_PREVIEW_HEIGHT_DESIGN
      + TASKS_VIEWPORT_BOTTOM_EXTENSION_DESIGN
      - DAY_TABS_FOOTER_RESERVE_DESIGN;
    this.tasks_scroll_container = new UIScrollableContainer(
      this.ctx,
      LayoutScaler.xFromDesign( viewport_x ),
      LayoutScaler.yFromDesign( viewport_y ),
      LayoutScaler.widthFromDesign( viewport_width ),
      LayoutScaler.heightFromDesign( viewport_height ),
      {
        direction: "vertical",
        scroll_speed: 1,
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 0.85,
        enable_rubber_band: true,
        rubber_band_resistance: 0.5,
        rubber_band_max_distance: LayoutScaler.heightFromDesign( 60 ),
        rubber_band_progression: 3,
        viewport_corner_radius: LayoutScaler.cornerRadiusFromDesign(
          TASKS_VIEWPORT_CORNER_RADIUS_DESIGN
        ),
        fast_repaint: true,
      }
    );
    this.addChild( this.tasks_scroll_container );
  }

  private async claimTask( task_id: string ): Promise<boolean>
  {
    const stats_before = StatisticsStorage.load();
    const day = this.selected_day;
    const ok = await DailyTasksService.claim( task_id, day );
    if ( !ok ) return false;

    const card = this.task_cards.find( item => item.getConfig().task_id === task_id );
    const task = DailyTasksService.findTask( task_id, day );
    if ( card && task && this.content_root )
      this.playTaskClaimEffects( card, task, stats_before, this.content_root );
    else
      this.refreshStarsFromCache();
    return true;
  }

  private playTaskClaimEffects( card: DailyTaskCard, task: DailyTaskDto, stats_before: GameStatistics, root: UIElementHolder ): void
  {
    const reward_anchor = card.getRewardAnchor();
    if ( reward_anchor )
    {
      const sources: RewardFlySource[] = task.rewards.map( reward =>
      {
        const is_heart = reward.type === "heart" || reward.type === "infinite_hearts";
        return {
          target: reward.type === "coin" ? "coin" : is_heart ? "heart" : "avatar",
          img_id: dailyTaskRewardIcon( reward.type ),
          center: reward_anchor.center,
          size: reward_anchor.size,
          amount: reward.amount,
          copies: reward.type === "heart" ? Math.max( 1, Math.min( 3, reward.amount ) ) : 1,
        };
      } );
      RewardFlyToHud.play( this.ctx, root, sources, stats_before );
    }

    const star_anchor = card.getStarAnchor();
    const star_target = this.progress_star ? DailyTasksScreen.absoluteCenter( this.progress_star ) : null;
    if ( star_anchor && star_target )
    {
      const count = Math.max( 1, Math.min( 5, task.stars ?? 5 ) );
      StarFly.play( this.ctx, root, star_anchor.center, star_target, count, performance.now(), () => this.refreshStarsFromCache() );
    }
    else
      this.refreshStarsFromCache();
  }

  private refreshStarsFromCache(): void
  {
    this.stars_view = DailyTasksService.getCached()?.stars ?? this.stars_view;
    this.renderStars();
    this.forceMakeShouldBeRendered();
  }

  private static absoluteCenter( element: UIElement ): Vector2
  {
    const pos = element.getAbsolutePosition();
    const rect = element.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }

  private createTaskCards(): void
  {
    if ( !this.tasks_scroll_container ) return;

    for ( const card of this.task_cards )
      this.tasks_scroll_container.removeScrollableChild( card );

    this.task_cards = [];
    this.tasks_scroll_container.scrollTo( 0, false );
    const day_config = this.getSelectedDayConfig();
    const task_configs = day_config.tasks;
    this.updateEmptyDayText( day_config );

    task_configs.forEach( ( config, index ) =>
    {
      const card = new DailyTaskCard(
        this.ctx,
        0,
        LayoutScaler.heightFromDesign( TASKS_VIEWPORT_TOP_PAD_DESIGN + index * TASK_CARD_HEIGHT_DESIGN ),
        LayoutScaler.widthFromDesign( TASK_CARD_WIDTH_DESIGN ),
        LayoutScaler.heightFromDesign( TASK_CARD_HEIGHT_DESIGN ),
        config,
        task_id => this.claimTask( task_id ),
        () =>
        {
          void audioController.playSound( AudioClips.UI_CLICK );
          this.saveCurrentDayTasks();
          this.updateCompletedTasksBadge();
        }
      );
      this.task_cards.push( card );
      this.tasks_scroll_container!.addScrollableChild( card );
    } );

    this.tasks_scroll_container.setContentHeight(
      LayoutScaler.heightFromDesign(
        TASKS_VIEWPORT_TOP_PAD_DESIGN + task_configs.length * TASK_CARD_HEIGHT_DESIGN
      )
    );
  }

  private saveCurrentDayTasks(): void
  {
    this.getSelectedDayConfig().tasks = this.task_cards.map(
      card => card.getConfig()
    );
  }

  /** A day whose lineup the server has not sent still opens, with a hint instead of cards. */
  private updateEmptyDayText( day_config: DailyTaskDayConfig ): void
  {
    if ( !this.empty_day_text )
    {
      // Over the tasks viewport (see createTasksScrollContainer), two cards tall.
      this.empty_day_text = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 71 + 60 ),
        LayoutScaler.yFromDesign( 1001 ),
        LayoutScaler.widthFromDesign( TASK_CARD_WIDTH_DESIGN - 120 ),
        LayoutScaler.heightFromDesign( TASK_CARD_HEIGHT_DESIGN * 2 ),
        {
          text: "",
          font_size: LayoutScaler.textSizeFromDesign( 46 ),
          min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
          outline_color: "#0E2D92",
          auto_wrap: true,
          fit_font_size: true,
        }
      );
      this.empty_day_text.setClickable( false );
      this.addChild( this.empty_day_text );
    }
    const empty = day_config.tasks.length === 0 && this.payload_key !== "";
    this.empty_day_text.setText( empty
      ? LocalizationManager.tWithParams( "DAILY_TASKS.DAY_TASKS_LATER", [ String( day_config.day ) ] )
      : "" );
  }

  private addTaskText(
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    font_size: number,
    color: string,
    outline: string,
    outline_width: number,
    text_align: MyTextAlign = MyTextAlign.Left,
    parent?: UIElement
  ): UITextElement
  {
    const text_element = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      {
        text,
        font_size: LayoutScaler.textSizeFromDesign( font_size ),
        min_font_size: LayoutScaler.textSizeFromDesign( Math.max( 20, font_size - 10 ) ),
        font_weight: 900,
        text_color: color,
        text_style: TextStyleScheme.geologica,
        text_align,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( outline_width ),
        outline_color: outline,
        auto_wrap: false,
        fit_font_size: true,
      }
    );

    if ( parent )
      parent.addChild( text_element );
    else
      this.addChild( text_element );
    return text_element;
  }

  private updateCompletedTasksBadge(): void
  {
    const day_config = this.getSelectedDayConfig();
    day_config.completed_tasks = day_config.locked ? 0 : this.getCompletedTasksCount();
    this.day_tabs_foreground_layer?.removeAllChildren();
    this.createCurrentDayBackgroundShadowCover();
    this.createDaySelector();
    this.createDaySelectorStatuses();
    this.forceMakeShouldBeRendered();
  }

  private getDayTabsYOffset(): number
  {
    return 65 + this.tasks_panel_extra_height - this.day_tabs_up_shift_design;
  }

  private createDayTabsBackgroundLayer(): void
  {
    this.day_tabs_background_layer = new UIElementHolder(
      this.ctx, 0, 0, this.content_width, this.rect.getH()
    );
    this.day_tabs_background_layer.setClickable( false );
    this.addChild( this.day_tabs_background_layer );
  }

  private createDayTabsForegroundLayer(): void
  {
    this.day_tabs_foreground_layer = new DayTabsLayer(
      this.ctx,
      0,
      0,
      this.content_width,
      this.rect.getH(),
      LayoutScaler.yFromDesign( 1580 + this.getDayTabsYOffset() )
    );
    this.addChild( this.day_tabs_foreground_layer );
  }

  private getSelectedDayConfig(): DailyTaskDayConfig
  {
    return this.day_configs.find( config => config.day === this.selected_day )
      ?? this.day_configs[0];
  }

  private selectDay( selected_day: number ): void
  {
    if ( selected_day === this.selected_day
      || !this.day_configs.some( config => config.day === selected_day ) )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );
    DailyTaskCard.hideLockedTooltip();
    this.saveCurrentDayTasks();
    this.selected_day = selected_day;
    this.createTaskCards();

    this.day_tabs_background_layer?.removeAllChildren();
    this.day_tabs_foreground_layer?.removeAllChildren();

    this.createDaySelectorBackgrounds();
    this.createCurrentDayBackgroundShadowCover();
    this.createDaySelector();
    this.createDaySelectorStatuses();
    this.forceMakeShouldBeRendered();
  }

  private createDaySelector(): void
  {
    const day_step = 122.49;
    const inactive_width = 110;
    const width_extra = 4;
    const height_extra = 24;
    const y_offset = -24;
    const inactive_main_height = 153;
    const inactive_level_offset = 6.4;
    const inactive_content_y_offset = -15 + inactive_level_offset;
    // The main PNG has large transparent top/bottom margins. This places its
    // visible shield inside the background with the proportions from the ref.
    const inactive_main_y_offset = -55.97 + inactive_level_offset;

    for ( const config of this.day_configs )
    {
      const day = config.day;
      const is_current = day === this.selected_day;
      const day_center_x = 173.06 + ( day - 1 ) * day_step;
      const selector_width = is_current ? 123 : inactive_width + width_extra;
      const selector_width_px = LayoutScaler.widthFromDesign( selector_width );
      const selector_center_px = LayoutScaler.xFromDesign( day_center_x );
      const selector_x_px = selector_center_px - selector_width_px / 2;

      if ( is_current )
      {
        // Match the inactive background's opaque width despite the different
        // transparent horizontal padding in the selected PNG.
        const height = 138.18 + height_extra;
        const image = new UIImage(
          this.ctx,
          selector_x_px,
          LayoutScaler.yFromDesign( 1632.51 + y_offset + this.getDayTabsYOffset() ),
          selector_width_px,
          LayoutScaler.heightFromDesign( height ),
          ImgID.UI_DailyTasksCurrentDaySelected
        );
        image.setClickable( false );
        this.day_tabs_foreground_layer?.addChild( image );
      }
      else
      {
        const height = inactive_main_height;
        const image = new UIImage(
          this.ctx,
          selector_x_px,
          LayoutScaler.yFromDesign(
            1662.36 + inactive_main_y_offset + this.getDayTabsYOffset()
          ),
          selector_width_px,
          LayoutScaler.heightFromDesign( height ),
          ImgID.UI_DailyTasksNotSelectedDayMain,
          {},
          true
        );
        image.setOnClick( () => this.selectDay( day ) );
        this.day_tabs_foreground_layer?.addChild( image );
      }

      this.createDayLabel(
        day,
        is_current,
        is_current ? y_offset : inactive_content_y_offset
      );
    }
  }

  private createDaySelectorStatuses(): void
  {
    const lock_width = 74;
    const lock_height = 88;
    const day_step = 122.49;

    for ( const config of this.day_configs )
    {
      if ( !config.locked ) continue;

      const lock = new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( 503 + ( config.day - 4 ) * day_step ),
        LayoutScaler.yFromDesign(
          1797 + this.tasks_panel_extra_height - this.day_tabs_up_shift_design
        ),
        LayoutScaler.widthFromDesign( lock_width ),
        LayoutScaler.heightFromDesign( lock_height ),
        ImgID.UI_DailyTasksDayLock
      );
      lock.setClickable( false );
      this.day_tabs_foreground_layer?.addChild( lock );
    }

    const badge_size = 74;
    for ( const config of this.day_configs )
    {
      if ( config.locked || config.completed_tasks <= 0 )
        continue;

      const is_current = config.day === this.selected_day;
      const badge_x = 173.06 + ( config.day - 1 ) * day_step - badge_size / 2;
      const badge_y = 1811
        + this.tasks_panel_extra_height
        - this.day_tabs_up_shift_design
        + ( is_current ? -8 : 0 );
      const badge_image = new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( badge_x ),
        LayoutScaler.yFromDesign( badge_y ),
        LayoutScaler.widthFromDesign( badge_size ),
        LayoutScaler.heightFromDesign( badge_size ),
        ImgID.UI_DailyTasksDayBadge
      );
      badge_image.setClickable( false );
      this.day_tabs_foreground_layer?.addChild( badge_image );
      const badge_text = this.addTaskText(
        String( config.completed_tasks ),
        badge_x,
        badge_y,
        badge_size,
        badge_size,
        40,
        "#FFFFFF",
        "#B51225",
        6,
        MyTextAlign.Center,
        this.day_tabs_foreground_layer
      );
      badge_text.setClickable( false );

    }
  }

  private getCompletedTasksCount(): number
  {
    return this.task_cards.filter(
      card => card.getState() === DailyTaskState.Completed
    ).length;
  }

  private createDaySelectorBackgrounds(): void
  {
    const day_step = 122.49;
    const inactive_bg_width = 116.65;
    const width_extra = 4;
    const inactive_bg_height = 152;
    const y_offset = -24;
    const inactive_level_offset = 6.4;
    // Grow upward while preserving the previous opaque bottom edge.
    const inactive_bg_y_adjustment = -10.94;

    for ( const config of this.day_configs )
    {
      const day = config.day;
      const is_current = day === this.selected_day;
      const day_center_x = 173.06 + ( day - 1 ) * day_step;
      const background_width = inactive_bg_width + width_extra;

      if ( is_current )
      {
        const current_background = new UIImage(
          this.ctx,
          LayoutScaler.xFromDesign( day_center_x - background_width / 2 ),
          LayoutScaler.yFromDesign( 1632.98 + this.getDayTabsYOffset() ),
          LayoutScaler.widthFromDesign( background_width ),
          LayoutScaler.heightFromDesign( 140.02 ),
          ImgID.UI_DailyTasksCurrentDayBg
        );
        current_background.setClickable( false );
        this.day_tabs_background_layer?.addChild( current_background );
        continue;
      }

      const image = new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( day_center_x - background_width / 2 ),
        LayoutScaler.yFromDesign(
          1656.98
            + y_offset
            + inactive_level_offset
            + inactive_bg_y_adjustment
            + this.getDayTabsYOffset()
        ),
        LayoutScaler.widthFromDesign( background_width ),
        LayoutScaler.heightFromDesign( inactive_bg_height ),
        ImgID.UI_DailyTasksNotSelectedDayBg
      );
      image.setClickable( false );
      this.day_tabs_background_layer?.addChild( image );
    }
  }

  private createDayLabel(
    day: number,
    is_current: boolean,
    y_offset: number
  ): void
  {
    const day_center_x = 173.06 + ( day - 1 ) * 122.49;
    const text_width = is_current ? 116 : 106;
    const text_x = day_center_x - text_width / 2;
    const label_y = ( is_current ? 1649.36 : 1658 + y_offset )
      + this.getDayTabsYOffset();
    const label_height = 40;
    const number_y = ( is_current ? 1673.36 : 1682 + y_offset )
      + this.getDayTabsYOffset();
    const number_height = 75;

    const day_label = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( text_x ),
      LayoutScaler.yFromDesign( label_y ),
      LayoutScaler.widthFromDesign( text_width ),
      LayoutScaler.heightFromDesign( label_height ),
      {
        localization_key: "DAY",
        font_size: LayoutScaler.textSizeFromDesign( 31 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 24 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 6 ),
        outline_color: "#131E48",
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    day_label.setClickable( false );
    this.day_tabs_foreground_layer?.addChild( day_label );

    const day_number = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( text_x ),
      LayoutScaler.yFromDesign( number_y ),
      LayoutScaler.widthFromDesign( text_width ),
      LayoutScaler.heightFromDesign( number_height ),
      {
        text: String( day ),
        font_size: LayoutScaler.textSizeFromDesign( 62 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#131E48",
        auto_wrap: false,
        fit_font_size: true,
      }
    );
    day_number.setClickable( false );
    this.day_tabs_foreground_layer?.addChild( day_number );
  }

  private createCurrentDayBackgroundShadowCover(): void
  {
    const width_extra = 4;
    const height_extra = 24;
    const y_offset = -24;
    const tasks_bg_y = 968;
    const tasks_bg_height = 738.22
      + this.tasks_panel_extra_height
      - DAY_TABS_FOOTER_RESERVE_DESIGN;
    const current_day = this.selected_day;
    const y = 1656.98 + y_offset + this.getDayTabsYOffset();
    const width = 116.65 + width_extra;
    const day_center_x = 173.06 + ( current_day - 1 ) * 122.49;
    const x = day_center_x - width / 2;
    const height = 116.02 + height_extra;
    // In tasks_bg.png rows 186..195 are the baked white bottom shadow.
    // The bottom nine-slice cap is rendered 1:1, so its screen band is 12px.
    const shadow_height = 12;
    const shadow_y = tasks_bg_y + tasks_bg_height - shadow_height;

    const shadow_cover = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      ImgID.UI_DailyTasksCurrentDayBg
    );
    shadow_cover.setRenderClip(
      0,
      LayoutScaler.yFromDesign( shadow_y - y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( shadow_height )
    );
    shadow_cover.setClickable( false );
    this.day_tabs_foreground_layer?.addChild( shadow_cover );
  }

  /** Adds screen artwork to the centered design-width composition. */
  public override addChild(
    element: UIElement,
    is_part_of_parent_not_a_separate_element: boolean = false
  ): void
  {
    if ( this.content_root )
    {
      this.content_root.addChild( element, is_part_of_parent_not_a_separate_element );
      return;
    }

    super.addChild( element, is_part_of_parent_not_a_separate_element );
  }

  private initializeEventsController(): void
  {
    this.events_controller = new BasicEventsController( this.ctx.canvas );
    this.events_controller.init(
      p => super.onTouchStart( p, performance.now() ),
      p => super.onTouchEnd( p, performance.now() ),
      p => super.onTouchMove( p, performance.now() )
    );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.updateTimerText( now );

    if ( this.week_end_ms > 0
      && Date.now() >= this.week_end_ms
      && now - this.rollover_attempt_at > 15_000 )
    {
      this.rollover_attempt_at = now;
      void this.refreshAfterWeekRollover();
    }
  }

  private async refreshAfterWeekRollover(): Promise<void>
  {
    const payload = await DailyTasksService.fetch();
    const end = payload?.stars?.week_end ? Date.parse( payload.stars.week_end ) : 0;
    if ( end > Date.now() )
      await this.loadFromServer( false );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
    // A modal reward backdrop already covers the entire canvas. Painting the
    // decorative safe-area tint after it would create bright top/bottom bands.
    if ( this.dom_mode )
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost(), !this.reward_popup_open );
    else if ( !this.reward_popup_open )
      SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public canReuseScreen(): boolean
  {
    return !this.reward_popup_open && this.payload_key !== "";
  }

  public onScreenHidden(): void
  {
    this.events_controller?.disable();
  }

  public onScreenShown(): void
  {
    this.events_controller?.enable();
    DailyTasksAnalytics.opened( DailyTasksService.getCached() );
    void this.loadFromServer( true );
  }

  public destroy(): void
  {
    this.events_controller?.cleanup();
    this.events_controller = null;
  }

  public name(): string
  {
    return "DailyTasksScreen";
  }
}

class DayTabsLayer extends UIElementHolder
{
  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    private readonly interaction_top: number
  )
  {
    super( ctx, x, y, width, height );
  }

  protected override containsPointInner( point: Vector2 ): boolean
  {
    return point.x >= 0
      && point.x <= this.getRect().getW()
      && point.y >= this.interaction_top
      && point.y <= this.getRect().getH();
  }
}
